import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that requires authentication and email verification
 * Redirects to /auth if user is not logged in or email is not verified
 * Redirects pure employees (not promoted) to /employee/dashboard
 * Shows loading spinner while checking auth state
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, emailVerified } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check if email is verified
  if (!emailVerified) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user has an organization
  if (!user.organizationId) {
    // User exists but has no organization - redirect to no-organization page
    return <Navigate to="/no-organization" replace />;
  }

  // Check if user is a pure employee (role is 'employee' and they're not on employee routes)
  // Promoted employees (with staff roles like manager/admin) can access staff routes
  const isEmployeeOnlyRole = user.role === 'employee';
  const isEmployeeRoute = location.pathname.startsWith('/employee');

  if (isEmployeeOnlyRole && !isEmployeeRoute) {
    // Redirect pure employees to their dashboard
    return <Navigate to="/employee/dashboard" replace />;
  }

  return <>{children}</>;
}
